import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { DEFAULT_MODE, parsePath, pathFor, type Lang } from './lib/routes';
import { translate } from './i18n';
import { applySeo } from './lib/seo';
import { trackPageview } from './lib/analytics';

interface AppState {
  lang: Lang;
  mode: string;
  navigate: (lang: Lang, mode: string) => void;
  setMode: (mode: string) => void;
  setLang: (lang: Lang) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
}

const Ctx = createContext<AppState | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const [route, setRoute] = useState(() =>
    typeof window === 'undefined' ? { lang: 'en' as Lang, mode: DEFAULT_MODE } : parsePath(window.location.pathname),
  );

  // keep <head> in sync on every route change (best-practice §5.2), then send a
  // pageview — but skip the first run, which gtag('config') already counted (§10).
  const firstRun = useRef(true);
  useEffect(() => {
    applySeo(route.lang, route.mode);
    if (firstRun.current) {
      firstRun.current = false;
      // still tag the initial Clarity session with lang/mode
      window.clarity?.('set', 'lang', route.lang);
      window.clarity?.('set', 'mode', route.mode);
    } else {
      trackPageview(route.lang, route.mode);
    }
  }, [route.lang, route.mode]);

  useEffect(() => {
    const onPop = () => setRoute(parsePath(window.location.pathname));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = useCallback((lang: Lang, mode: string) => {
    const path = pathFor(lang, mode);
    if (path !== window.location.pathname) window.history.pushState(null, '', path);
    setRoute({ lang, mode });
    window.scrollTo(0, 0);
  }, []);

  const setMode = useCallback((mode: string) => navigate(route.lang, mode), [navigate, route.lang]);
  const setLang = useCallback((lang: Lang) => navigate(lang, route.mode), [navigate, route.mode]);

  const t = useCallback(
    (key: string, vars?: Record<string, string | number>) => translate(route.lang, key, vars),
    [route.lang],
  );

  return (
    <Ctx.Provider value={{ lang: route.lang, mode: route.mode, navigate, setMode, setLang, t }}>
      {children}
    </Ctx.Provider>
  );
}

export function useApp(): AppState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useApp must be used within AppProvider');
  return v;
}
